import { createCursorStore } from 'fynx';
import { fromJS, Map } from 'immutable';
import { Cursor } from 'immutable/contrib/cursor';
import { IContractor } from './models/contractorModel';
import { IDoorSize } from './models/doorSizeModel';
import { IEmployee } from './models/employeeModel';
import { IFile } from './models/fileModel';
import { IFiscalYear } from './models/fiscalYearModel';
import { IGlassType } from './models/glassTypeModel';
import { IJobCost } from './models/jobCostModel';
import { IJob } from './models/jobModel';
import { IKeyNameModel } from './models/keyNameModel';
import { IMessage } from './models/messageModel';
import { ISiteVisit } from './models/siteVisitModel';
import { ISystemModule } from './models/systemModuleModel';
import { IVendor } from './models/vendorModel';

type storeFunc = {
	(): Cursor,
	listen(any): void;
}

export type StoreInstance = Cursor;

const storeFactory = createCursorStore(fromJS({
	viewState: {
		alerts: [], // list of ui alerts
		banner: undefined,
		bootstrapping: false,
		currentUser: undefined,
		currentEmployee: undefined,
		globalSave: {
			state: 0 // 0 = disabled, 1 = enabled, 2 = saving
		},
		headers: {}, // holds list of header components
		headerActions: {}, // holds list of action components
		metrics: {
			windowSize: { width: 0, height: 0 }
		},
		modals: [], // holds stack of modal content components
		navConfirmationHandler: undefined, // optional navigation confirmation handler func(message, callback)
		watchedChannels: [],
		pinnedStats: {}, // holds the special status for job costs
		printChildren: null, // holds children for the PrintRoot component
		routePath: '',
		routeState: undefined,
		scrollContentKey: undefined, // changing this key triggers the main content div to scroll-top
		searchResults: {},
		sidebar: {
			expanded: false
		},
		tooltip: undefined
	},

	model: {
		config: {},
		contractors: {},
		domainEntities: {
			modules: [],
			orgTitles: [],
			orgDepartments: [],
			glassTypes: [],
			doorSizes: [],
			subTradeTypes: []
		},
		employees: {},
		files: {},
		fiscalYears: {},
		jobCosts: {},
		jobs: {},
		messages: {},
		siteVisits: {},
		vendors: {}
	}

})) as storeFunc;

export default storeFactory;

export enum ModelCollection {
	contractors = 'contractors',
	employees = 'employees',
	files = 'files',
	fiscalYears = 'fiscalYears',
	jobCosts = 'jobCosts',
	jobs = 'jobs',
	messages = 'messages',
	siteVisits = 'siteVisits',
	vendors = 'vendors'
}



export function getCollection({ store, collection}: { store?: StoreInstance, collection: ModelCollection.contractors }): Map<string, IContractor>;
export function getCollection({ store, collection}: { store?: StoreInstance, collection: ModelCollection.employees }): Map<string, IEmployee>;
export function getCollection({ store, collection}: { store?: StoreInstance, collection: ModelCollection.files }): Map<string, IFile>;
export function getCollection({ store, collection}: { store?: StoreInstance, collection: ModelCollection.fiscalYears }): Map<string, IFiscalYear>;
export function getCollection({ store, collection}: { store?: StoreInstance, collection: ModelCollection.jobCosts }): Map<string, IJobCost>;
export function getCollection({ store, collection}: { store?: StoreInstance, collection: ModelCollection.jobs }): Map<string, IJob>;
export function getCollection({ store, collection}: { store?: StoreInstance, collection: ModelCollection.messages }): Map<string, IMessage>;
export function getCollection({ store, collection}: { store?: StoreInstance, collection: ModelCollection.siteVisits }): Map<string, ISiteVisit>;
export function getCollection({ store, collection}: { store?: StoreInstance, collection: ModelCollection.vendors }): Map<string, IVendor>;
export function getCollection({ store = storeFactory(), collection}: { store?: StoreInstance, collection: ModelCollection }) {
	return store.getIn(['model', collection])
}

export function getModel({ store, collection, key}: { store?: StoreInstance, collection: ModelCollection.contractors, key: string }): IContractor;
export function getModel({ store, collection, key}: { store?: StoreInstance, collection: ModelCollection.employees, key: string }): IEmployee;
export function getModel({ store, collection, key}: { store?: StoreInstance, collection: ModelCollection.files, key: string }): IFile;
export function getModel({ store, collection, key}: { store?: StoreInstance, collection: ModelCollection.fiscalYears, key: string }): IFiscalYear;
export function getModel({ store, collection, key}: { store?: StoreInstance, collection: ModelCollection.jobCosts, key: string }): IJobCost;
export function getModel({ store, collection, key}: { store?: StoreInstance, collection: ModelCollection.jobs, key: string }): IJob;
export function getModel({ store, collection, key}: { store?: StoreInstance, collection: ModelCollection.messages, key: string }): IMessage;
export function getModel({ store, collection, key}: { store?: StoreInstance, collection: ModelCollection.siteVisits, key: string }): ISiteVisit;
export function getModel({ store, collection, key}: { store?: StoreInstance, collection: ModelCollection.vendors, key: string }): IVendor;
export function getModel({ store = storeFactory(), collection, key}: { store?: StoreInstance, collection: ModelCollection, key: string }) {
	return store.getIn(['model', collection, key]);
}

export enum DomainEntityType {
	modules = 'modules',
	orgTitles = 'orgTitles',
	orgDepartments = 'orgDepartments',
	glassTypes = 'glassTypes',
	doorSizes = 'doorSizes',
	subTradeTypes = 'subTradeTypes',
	leadSources = 'leadSources'
}

export function getDomainEntities({ store, type}: { store?: StoreInstance, type: DomainEntityType.doorSizes }): IDoorSize[];
export function getDomainEntities({ store, type}: { store?: StoreInstance, type: DomainEntityType.glassTypes }): IGlassType[];
export function getDomainEntities({ store, type}: { store?: StoreInstance, type: DomainEntityType.modules }): ISystemModule[];
export function getDomainEntities({ store, type}: { store?: StoreInstance, type: DomainEntityType.subTradeTypes }): IKeyNameModel[];
export function getDomainEntities({ store, type}: { store?: StoreInstance, type: DomainEntityType.orgTitles }): IKeyNameModel[];
export function getDomainEntities({ store, type}: { store?: StoreInstance, type: DomainEntityType.orgDepartments }): IKeyNameModel[];
export function getDomainEntities({ store, type}: { store?: StoreInstance, type: DomainEntityType.leadSources }): { key: string, name: string }[];
export function getDomainEntities({ store = storeFactory(), type}: { store?: StoreInstance, type: DomainEntityType }) {
	return store.getIn(['model', 'domainEntities'])[type];
}